import { motion } from 'framer-motion';
import { MapPin, Truck, Globe, Navigation } from 'lucide-react';

const regions = [
  'Deutschland',
  'Österreich',
  'Schweiz',
  'Niederlande',
  'Belgien',
  'Frankreich',
  'Polen',
  'Tschechien',
];

export const Location = () => {
  return (
    <section id="standort" data-testid="location-section" className="py-24 md:py-32 bg-tiktok-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-tiktok-cyan/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-tiktok-pink/5 rounded-full blur-[150px]" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Map Placeholder - No external services */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-tiktok-surface to-tiktok-elevated">
              {/* Static Map Placeholder */}
              <div className="w-full h-[400px] flex flex-col items-center justify-center p-8 relative">
                {/* Decorative Europe Shape */}
                <div className="absolute inset-0 flex items-center justify-center opacity-10">
                  <svg viewBox="0 0 400 300" className="w-full h-full">
                    <path 
                      d="M200,50 Q250,30 280,60 Q320,80 310,120 Q340,140 320,180 Q300,220 260,230 Q220,250 180,240 Q140,230 120,200 Q80,180 90,140 Q70,100 100,80 Q130,50 170,55 Z" 
                      fill="currentColor" 
                      className="text-tiktok-cyan"
                    />
                  </svg>
                </div>

                {/* Content */}
                <div className="relative z-10 text-center">
                  <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-tiktok-cyan/30 to-tiktok-pink/30 flex items-center justify-center mx-auto mb-6 border border-white/10">
                    <Globe className="w-10 h-10 text-tiktok-cyan" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Europaweiter Service</h3>
                  <p className="text-gray-400 mb-6 max-w-xs mx-auto">
                    Wir sind in ganz Deutschland und Europa für Sie unterwegs.
                  </p>
                  
                  {/* Location badges */}
                  <div className="flex flex-wrap justify-center gap-2">
                    <span className="px-3 py-1 bg-tiktok-cyan/20 rounded-full text-sm text-tiktok-cyan border border-tiktok-cyan/30">
                      Deutschland
                    </span>
                    <span className="px-3 py-1 bg-tiktok-pink/20 rounded-full text-sm text-tiktok-pink border border-tiktok-pink/30">
                      Europa
                    </span>
                  </div>
                </div>
              </div>

              {/* Bottom Info Bar */}
              <div className="bg-tiktok-black/90 p-4 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-tiktok-cyan to-tiktok-pink flex items-center justify-center">
                    <Navigation className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Europaweiter Ankauf</p>
                    <p className="text-sm text-gray-400">Kostenlose Abholung überall</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-semibold uppercase tracking-[0.25em] text-tiktok-cyan mb-6 px-4 py-2 bg-tiktok-cyan/10 rounded-full border border-tiktok-cyan/20">
              Unser Einzugsgebiet
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Wir sind in{' '}
              <span className="bg-gradient-to-r from-tiktok-cyan to-tiktok-pink bg-clip-text text-transparent">
                Ihrer Region
              </span>{' '}
              unterwegs
            </h2>
            <p className="text-gray-300 text-lg mb-8">
              Ob kurze oder lange Strecke – für uns ist alles kein Problem. Wir sind deutschlandweit 
              und europaweit für Sie im Einsatz und holen Ihr Fahrzeug direkt bei Ihnen ab.
            </p>

            {/* Service Info Cards */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-4 p-4 bg-tiktok-surface rounded-xl border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-tiktok-cyan/20 to-tiktok-pink/20 flex items-center justify-center">
                  <Truck className="w-6 h-6 text-tiktok-cyan" />
                </div>
                <div>
                  <p className="text-white font-semibold">Kostenlose Abholung</p>
                  <p className="text-sm text-gray-400">Überall in Deutschland & Europa</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-tiktok-surface rounded-xl border border-white/10">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-tiktok-cyan/20 to-tiktok-pink/20 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-tiktok-pink" />
                </div>
                <div>
                  <p className="text-white font-semibold">Keine Entfernung zu weit</p>
                  <p className="text-sm text-gray-400">Kurze oder lange Strecke</p>
                </div>
              </div>
            </div>

            {/* Regions */}
            <div className="space-y-3">
              <p className="text-sm text-gray-400 uppercase tracking-wider font-semibold">
                Länder & Regionen
              </p>
              <div className="flex flex-wrap gap-2">
                {regions.map((region, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="region-badge px-4 py-2 bg-tiktok-surface border border-white/10 rounded-full text-sm text-gray-300 cursor-pointer hover:border-tiktok-cyan/50 transition-colors"
                  >
                    {region}
                  </motion.span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Location;
